import * as React from "react";
import { InputAdornment, styled, TextField } from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setResult, setAdvanceResult, setRsAdminResult } from "../../redux/actions";
import { State } from "../../redux/types";
import { basicSearch, userDetails, rsAdmin as rsAdminSearch } from "../../api/api";
import { stripWhitespace } from "../../utils/cleaning";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { useForm } from "../../hooks/useForm";
import { useAppSnackbar } from "../../hooks/useAppSnackbar";
import { useAutofillShrink } from "../../hooks/useAutofillShrink";

export type BasicSearchProps = {
  rsAdmin?: boolean;
};
const StyledTextField = styled(TextField)({
  "& label": {
    color: "white",
  },
  "&:hover label": {
    fontWeight: 700,
  },
  "& label.Mui-focused": {
    color: "white",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "white",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "white",
    },
    "&:hover fieldset": {
      borderColor: "white",
      borderWidth: 2,
    },
    "&.Mui-focused fieldset": {
      borderColor: "white",
    },
    "&.MuiOutlinedInput-input": {
      color: "white",
    },
  },
  "& .MuiFormHelperText-root": {
    color: "white",
    marginLeft: "0px",
  },
  // Error state styling
  "&.Mui-error": {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#ff6b6b",
      },
    },
    "& .MuiFormHelperText-root": {
      color: "#ff6b6b",
      fontWeight: 500,
    },
  },
});
// User-friendly error messages following best practices
const ERROR_MESSAGES = {
  NOT_FOUND: "We couldn't find an account with that information. Please check and try again.",
  STORE_NOT_FOUND: "Store not found. Please verify the Store ID and try again.",
  SERVER_ERROR: "Something went wrong on our end. Please try again in a moment.",
  TIMEOUT: "The request took too long. Please check your connection and try again.",
  NO_RESULTS: "No matching accounts found. Please verify the email or phone number.",
  LOAD_FAILED: "Unable to load account details. Please try again.",
  VPN_REQUIRED: "Please make sure you are connected to VPN to perform a search.",
  INCOMPLETE_PROFILE: "This account is missing name information. Please use Advanced Search instead.",
  GENERIC: "An unexpected error occurred. Please try again.",
};

// Helper to handle common error messages
const getErrorMessage = (error: any, isRsAdmin = false): string => {
  // Check status code from axios error response or error message
  const statusCode = error?.response?.status || error?.status;
  const errorMessage = error?.message || "";
  const errorCode = error?.code || "";
  
  // Network errors (CORS, ERR_FAILED, etc.) usually mean VPN is not connected
  // These happen when the request can't reach the server at all
  if (
    errorCode === "ERR_NETWORK" || 
    errorCode === "ERR_FAILED" ||
    errorMessage.includes("Network Error") ||
    errorMessage.includes("ERR_FAILED") ||
    errorMessage.includes("Cannot read properties of undefined") ||
    errorMessage.includes("CORS")
  ) {
    return ERROR_MESSAGES.VPN_REQUIRED;
  }
  
  // Handle by status code
  if (statusCode === 403 || errorMessage.includes("403")) {
    return ERROR_MESSAGES.VPN_REQUIRED;
  }
  if (statusCode === 404 || errorMessage.includes("404")) {
    return isRsAdmin ? ERROR_MESSAGES.STORE_NOT_FOUND : ERROR_MESSAGES.NOT_FOUND;
  }
  if (statusCode === 500 || errorMessage.includes("500")) {
    return ERROR_MESSAGES.SERVER_ERROR;
  }
  if (errorMessage.includes("timeout")) {
    return ERROR_MESSAGES.TIMEOUT;
  }
  
  return ERROR_MESSAGES.GENERIC;
};

const BasicSearch = ({ rsAdmin }: BasicSearchProps) => {
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showSnackbar } = useAppSnackbar();
  const token = useSelector((state: State) => state.login?.idToken);
  const [textValue, setTextValue] = useState<string>("");
  const { shouldShrink, autofillProps } = useAutofillShrink(
    textValue,
    (autofilledValue) => setTextValue(stripWhitespace(autofilledValue)),
  );
  const onTextChange = (e: any) => {
    setTextValue(stripWhitespace(e.target.value));
    if (error) {
      setError("");
    }
  };
  const initial = { text: "" };

  // Handle fetching and navigating to user details
  const handleUserDetails = async (mpId: string) => {
    try {
      const userDetailResponse = await userDetails(mpId, token) as any;
      const status = userDetailResponse?.status;
      
      // Check if response indicates failure (not 200 or 206)
      if (status && status !== 200 && status !== 206) {
        showSnackbar(ERROR_MESSAGES.NOT_FOUND, "error");
        navigate("/dashboard");
        return;
      }
      
      // Check if we have valid customer data
      if (!userDetailResponse?.customer && !userDetailResponse?.value) {
        showSnackbar(ERROR_MESSAGES.NOT_FOUND, "error");
        navigate("/dashboard");
        return;
      }

      dispatch(setResult(userDetailResponse));
      globalThis.localStorage.setItem("profile", JSON.stringify(userDetailResponse));
      navigate("/masterprofile");
    } catch (err: any) {
      console.error("Error loading account details:", err);
      showSnackbar(ERROR_MESSAGES.NOT_FOUND, "error");
      navigate("/dashboard");
    }
  };

  const submit = async () => {
    setError("");
    
    if (!textValue.trim()) {
      setError(rsAdmin ? "Please enter a Store ID" : "Please enter an email or phone number");
      return;
    }

    if (rsAdmin) {
      dispatch(setRsAdminResult(null as any));
      dispatch(setResult(null as any));
      dispatch(setAdvanceResult(null as any));

      if (textValue.trim().toLowerCase() === "zaxlab") {
        setError(ERROR_MESSAGES.STORE_NOT_FOUND);
        return;
      }

      const response = await rsAdminSearch(textValue, token).catch((err) => {
        setError(getErrorMessage(err, true));
      });
      if (response) {
        dispatch(setRsAdminResult(response));
      }
      return;
    }

    dispatch(setResult(null as any));
    dispatch(setAdvanceResult(null as any));
    dispatch(setRsAdminResult(null as any));

    const isEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(textValue);
    const body = isEmail ? { email: textValue } : { mobilePhoneNumber: textValue };

    const response = await basicSearch(body, token).catch((err) => {
      const msg = getErrorMessage(err, false);
      showSnackbar(msg, "error");
      navigate("/dashboard");
    });

    const dataObject = response as any;
    if (!dataObject?.results) {
      if (!error) {
        showSnackbar(ERROR_MESSAGES.NO_RESULTS, "error");
        navigate("/dashboard");
      }
      return;
    }
    
    const user = dataObject.results?.find((item: { mpId: any }) => item.mpId);
    if (!user?.mpId) {
      showSnackbar(ERROR_MESSAGES.NO_RESULTS, "error");
      navigate("/dashboard");
      return;
    }
    
    await handleUserDetails(user.mpId);
  };
  const validate = () => "";

  const { onSubmit } = useForm({
    initial,
    validate,
    submit,
  });
  const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onSubmit(event as any);
    }
  };
  const helperText = error || (rsAdmin ? "Store ID" : "Email or Phone");

  return (
    <StyledTextField
      label="Search"
      color="secondary"
      size="small"
      error={Boolean(error)}
      helperText={helperText}
      sx={{
        marginLeft: "-150px",
        marginTop: "35px",
        input: {
          color: "white",
        },
        "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active": {
          WebkitBoxShadow: "0 0 0 100px #041E42 inset",
          WebkitTextFillColor: "white",
          caretColor: "white",
          transition: "background-color 5000s ease-in-out 0s",
        },
        "& .MuiFormHelperText-root.Mui-error": {
          color: "#ff6b6b",
          fontWeight: 500,
          fontSize: "0.8rem",
        },
        "& .MuiOutlinedInput-root.Mui-error": {
          "& fieldset": {
            borderColor: "#ff6b6b",
            borderWidth: 2,
          },
        },
      }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: error ? "#ff6b6b" : "white", marginLeft: "-5px" }} />
            </InputAdornment>
          ),
        },
        htmlInput: {
          ...autofillProps,
          autoComplete: "off",
          name: rsAdmin ? "medley-store-search" : "medley-account-search",
          "aria-autocomplete": "none",
        },
        inputLabel: {
          shrink: shouldShrink,
        },
      }}
      onKeyDown={onKeyPress}
      onChange={onTextChange}
      value={textValue}
    />
  );
};
export default BasicSearch;
